import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Button, Paper, Typography } from '@mui/material';
import { useCoachExperience, isCoachRequestCancelled } from '../context/CoachExperienceContext';
import { coachStartAPI } from '../services/api';

export default function CoachExperienceEntry() {
  const experience = useCoachExperience();
  const headingId = useId();
  const [busy, setBusy] = useState(false);
  const [openRequested, setOpenRequested] = useState(false);
  const [error, setError] = useState('');
  const errorMessage = useRef(null);
  const attempt = useRef(0);
  const ownerId = experience?.ownerId;
  useLayoutEffect(() => {
    attempt.current += 1;
    setBusy(false);
    setOpenRequested(false);
    setError('');
    return () => { attempt.current += 1; };
  }, [ownerId]);
  useEffect(() => { if (error) errorMessage.current?.focus(); }, [error]);

  if (!experience || experience.accountChanged || experience.error
      || (!experience.data?.canTryStarter && !experience.data?.flags?.starter)) return null;

  async function enter() {
    if (busy) return;
    const currentAttempt = ++attempt.current;
    setBusy(true);
    setError('');
    try {
      if (!experience.data.flags.starter) {
        const result = await experience.run(options => coachStartAPI.start(options));
        if (result.data?.enabled !== true) throw new Error('The coaching experience could not be enabled.');
        const updated = await experience.refresh();
        if (currentAttempt !== attempt.current) return;
        if (!updated?.flags?.starter) throw new Error('Your coaching access could not be loaded. Please try again.');
      }
      if (currentAttempt === attempt.current) setOpenRequested(true);
    } catch (err) {
      if (currentAttempt === attempt.current && !isCoachRequestCancelled(err)) {
        setError(err.response?.data?.error || err.message || 'Could not open the new experience. Please try again.');
      }
    } finally {
      if (currentAttempt === attempt.current) setBusy(false);
    }
  }

  if (openRequested && !experience.loading && experience.data.flags.starter) {
    return <Navigate to="/coach-start" />;
  }

  return <Paper component="section" aria-labelledby={headingId} sx={{
    p: 2.5, mb: 2, background: 'var(--bg-card)', color: 'var(--text-primary)',
    border: '1px solid var(--border-color)', borderRadius: '12px', boxShadow: 'var(--shadow)',
  }}>
    <Typography component="h2" id={headingId} sx={{ fontSize: '18px', fontWeight: 700 }}>
      New coaching experience
    </Typography>
    <Typography component="p" sx={{ color: 'var(--text-secondary)', fontSize: '14px', my: 1 }}>
      Start with a coaching question. Your teams and saved work stay as they are.
    </Typography>
    {error && <Typography component="p" role="alert" tabIndex={-1} ref={errorMessage} sx={{ mb: 1 }}>{error}</Typography>}
    <Button onClick={enter} disabled={busy || experience.loading} sx={{
      minHeight: 44, color: 'var(--text-on-accent)', background: 'var(--accent)',
      textTransform: 'none', fontWeight: 700, px: 2,
      '&:hover': { background: 'var(--accent-hover, var(--accent))' },
      '&:focus-visible': { outline: '3px solid var(--accent)', outlineOffset: '3px' },
    }}>
      {busy ? 'Opening the new experience...' : 'Try the new experience'}
    </Button>
  </Paper>;
}
